import { useMutation } from '@apollo/client/react'
import {
  Alert,
  Box,
  Button,
  Center,
  Field,
  Input,
  InputProps,
  Link,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react'
import {
  SocialLoginButtons,
  SocialLoginButtonsProps,
} from '@src/components/sco/Account/UserLoginForm/SocialLoginButtons'
import { GenericFormProps, processSubmit } from '@src/components/sco/Form/formHelpers'
import { FormContainer } from '@src/components/sco/FormContainer'
import { ChevronRightIcon } from '@src/components/sco/icons'
import { NextLink } from '@src/components/sco/utilities/NextLink'
import { OrDivider } from '@src/components/sco/utilities/OrDivider'
import { RegisterUserInput, RegisterUserPayload } from '@src/graphql-types'
import { useCurrentUrl } from '@src/hooks/useCurrentUrl'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { TrackEvent } from '@src/lib/TrackEvent'
import { gql } from '@src/lib/gql'
import tracker from '@src/lib/tracker'
import { useRouter } from 'next/navigation'
import React, { ReactNode, useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'

export type UserSignupEmailCaptureFormFields = {
  email?: string
}

export type UserSignupEmailCaptureFormProps = GenericFormProps<UserSignupEmailCaptureFormFields> & {
  hubspotFormKey?: string
  /** Passed to registerUser as signupBrand (e.g. `clarity`). */
  brand?: string
  onLoginClick?: () => void
  onSocialLoginClick?: () => void
  renderLogin?: () => ReactNode
  defaultValues?: UserSignupEmailCaptureFormFields
  title?: ReactNode
  /** Overrides for social buttons (e.g. `buttonLayout: 'google-only'`). */
  socialLoginButtonsProps?: SocialLoginButtonsProps
}

export const UserSignupEmailCaptureForm = (props: UserSignupEmailCaptureFormProps) => {
  const router = useRouter()
  const currentFullUrl = useCurrentUrl()
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false)
  const [formErrorMessage, setFormErrorMessage] = useState<string>(null)
  const form = useForm<UserSignupEmailCaptureFormFields>({
    defaultValues: props.defaultValues,
    mode: 'onSubmit',
  })
  const { currentUser, refetchCurrentUser } = useCurrentUser()
  const [registerUser, { loading, error }] = useMutation(gql`
    mutation ($input: RegisterUserInput!) {
      registerUser(input: $input) {
        loginTokenSent
        viewer {
          id
          currentUser {
            id
            isRegistered
            isGuest
          }
        }
      }
    }
  `)

  const handleSubmit = useCallback(async (values: UserSignupEmailCaptureFormFields) => {
    const input: RegisterUserInput = {
      ...values,
      returnToUrl: currentFullUrl,
      hubspotFormKey: props.hubspotFormKey,
      brand: props.brand,
      sendConfirmationRequest: true,
    }

    return processSubmit({
      props,
      values,
      setFormErrorMessage,
      action: async (values: UserSignupEmailCaptureFormFields) => {
        return registerUser({
          variables: {
            input,
          },
        })
      },
      afterSuccess: async (
        values: UserSignupEmailCaptureFormFields,
        result: { data: { registerUser: RegisterUserPayload } }
      ) => {
        await tracker.emailCaptured({ ...values })
        await tracker.s0Submitted({ ...values })

        if (result.data?.registerUser?.loginTokenSent) {
          setFormErrorMessage(
            'This email is already registered. Please check your email for a link to login.'
          )
          return
        }

        if (props.returnToHref) {
          setIsRedirecting(true)
          await refetchCurrentUser()
          router.push(props.returnToHref)
        } else {
          await refetchCurrentUser()
          if (props.afterSuccess) {
            props.afterSuccess(values, result)
          }
        }
      },
    })
  }, [])

  return (
    <FormContainer {...props.containerProps}>
      <TrackEvent action={'s0Viewed'} onlyOnScreen={true} />
      {isRedirecting && (
        <Center padding={'1em'}>
          <Spinner />
        </Center>
      )}
      {!isRedirecting && (
        <>
          <Stack asChild align={'stretch'} direction={'column'} gap={'1em'}>
            <form onSubmit={form.handleSubmit(handleSubmit)}>
              {currentUser?.isRegistered && (
                <Alert.Root borderRadius={'md'} status={'warning'}>
                  <Alert.Indicator />
                  <Box textAlign={'left'}>
                    {"You're already logged in!"}{' '}
                    <Link asChild variant={'underline'}>
                      <NextLink href={'/account'} prefetch={false}>
                        View Account
                      </NextLink>
                    </Link>
                  </Box>
                </Alert.Root>
              )}
              {formErrorMessage && (
                <Alert.Root borderRadius={'md'} status={'error'}>
                  <Alert.Indicator marginRight={'1em'} />
                  <Box textAlign={'left'}>{formErrorMessage}</Box>
                </Alert.Root>
              )}
              <Field.Root invalid={!!form.formState.errors.email}>
                <Field.Label>Email Address</Field.Label>
                <Input
                  placeholder={'you@email.com'}
                  type={'email'}
                  {...(props.inputProps as InputProps)}
                  {...form.register('email', {
                    required: 'Email is required.',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                    onChange: e => {
                      if (
                        process.env.NEXT_PUBLIC_DEPLOYMENT_ENV !== 'production' &&
                        e.target.value === 'test++'
                      ) {
                        form.setValue(
                          'email',
                          // eslint-disable-next-line react-hooks/purity
                          `test+x${Math.round(Math.random() * 100_000_000_000)}@startups.com`,
                          {
                            shouldValidate: false,
                            shouldDirty: true,
                          }
                        )
                      }
                    },
                  })}
                />
                <Field.ErrorText>{form.formState.errors.email?.message}</Field.ErrorText>
              </Field.Root>
              <Box>
                <Button
                  loading={form.formState.isSubmitting}
                  size={'md'}
                  type={'submit'}
                  variant={'cta'}
                  width={'full'}
                  {...props.submitButtonProps}
                >
                  {props.submitButtonProps?.children || 'Submit'}
                  <ChevronRightIcon />
                </Button>
              </Box>
              <Box width={'full'}>
                <OrDivider marginBottom={2} />
                <SocialLoginButtons
                  actionLabel={'Sign Up'}
                  buttonLayout={'icons-only'}
                  hubspotFormKeys={props.hubspotFormKey ? [props.hubspotFormKey] : undefined}
                  returnToHref={props.returnToHref}
                  buttonProps={{
                    all: {
                      onClick: () => {
                        if (props.onSocialLoginClick) {
                          props.onSocialLoginClick()
                        }
                      },
                    },
                  }}
                  {...props.socialLoginButtonsProps}
                />
              </Box>
            </form>
          </Stack>
        </>
      )}
      <Box paddingTop={'0.5em'}>
        <Text color={'colorPalette.fg/50'} fontSize={'11px'} textAlign={'center'}>
          {'Submission confirms agreement to our'}{' '}
          <Link asChild color={'colorPalette.fg/60'} variant={'underline'}>
            <NextLink href={'/legal/terms-of-service'} target={'_blank'}>
              Terms of Service
            </NextLink>
          </Link>{' '}
          {'and'}{' '}
          <Link asChild color={'colorPalette.fg/60'} variant={'underline'}>
            <NextLink href={'/legal/privacy-policy'} target={'_blank'}>
              Privacy Policy
            </NextLink>
          </Link>
          .
        </Text>
      </Box>
    </FormContainer>
  )
}
